import { Card, Colors, MultilineInput, Separator, Text, TextInput } from '@amelio/ui-library';

import { OptionTranslationInput } from './OptionTranslationInput';

import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';
import { QuestionType } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getLanguageDisplayName, getLocalizedText } from '@/utils/localization';
import { filterOutOtherOption } from '@/utils/question';

interface QuestionTranslationCardProps {
    question: ReviewQuestion;
    defaultLanguage: string;
    targetLanguage: string;
    onQuestionLabelChange: (questionId: string, value: string) => void;
    onOptionLabelChange: (questionId: string, optionId: string, value: string) => void;
    previewEvaluee?: User;
    hasError?: boolean;
}

const LANGUAGE_DISPLAY_SUFFIX = ' :';

export const QuestionTranslationCard = ({
    question: { label, questionType, reviewQuestionOptions, id },
    defaultLanguage,
    targetLanguage,
    onQuestionLabelChange,
    onOptionLabelChange,
    previewEvaluee,
    hasError,
}: QuestionTranslationCardProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();

    const defaultLabel = replaceDynamicTags({
        text: label[defaultLanguage] || getLocalizedText(label) || '',
        employee: previewEvaluee,
    });
    const targetLabel = label[targetLanguage] || '';
    const languageDisplayName = getLanguageDisplayName(targetLanguage);
    const options = filterOutOtherOption(reviewQuestionOptions);

    const isRatingScale = questionType === QuestionType.RATING_SCALE;
    const hasOptions =
        questionType === QuestionType.CHECKBOX ||
        questionType === QuestionType.MULTI_CHOICE ||
        questionType === QuestionType.RANKING ||
        questionType === QuestionType.EMOJI_CHOICE;

    const ratingScaleLabels = [
        t('templateManagement.templateTranslation.fromLowerLabel'),
        t('templateManagement.templateTranslation.toUpperLabel'),
    ];

    return (
        <Card
            borderRadius={'2xl'}
            padding={'none'}
            bgColor={'bg-white'}
            dataTestId={'QuestionTranslationCard'}
            className={'p-6'}
        >
            <Text
                size={'default'}
                weight={'semibold'}
                color={Colors.SIDEBAR_TEXT_STRONG}
            >
                <QuestionTextRenderer text={defaultLabel} />
            </Text>

            <div
                className={
                    'mt-4 flex w-full flex-col items-start gap-4 md:mt-6 md:flex-row md:items-center'
                }
            >
                <Text
                    size={'small'}
                    weight={'medium'}
                    noWrap
                >
                    {`${languageDisplayName}${LANGUAGE_DISPLAY_SUFFIX}`}
                </Text>

                <div className={'w-full'}>
                    <MultilineInput
                        value={targetLabel}
                        onChange={(value: string) => onQuestionLabelChange(id ?? '', value)}
                        placeholder={t('templateManagement.templateTranslation.enterTranslation', {
                            language: languageDisplayName,
                        })}
                        variant={'white'}
                        size={'md'}
                        autoGrow={true}
                        rows={1}
                        dataTestId={'question-translation-multiline-input'}
                        className={'min-h-auto px-4 py-3 text-sm'}
                        error={hasError}
                    />
                </div>
            </div>

            {(isRatingScale || hasOptions) && (
                <Separator
                    spacing={'sm'}
                    thickness={'thin'}
                />
            )}

            {isRatingScale && options.length > 1 && (
                <div>
                    <Text
                        size={'small'}
                        weight={'medium'}
                        className={'mb-3'}
                    >
                        {t('templateManagement.templateTranslation.ratingScaleLabels')}
                    </Text>

                    <div className={'flex flex-col gap-4 md:flex-row'}>
                        {options.map((option, index) => (
                            <div
                                key={option.id}
                                className={'flex-1'}
                            >
                                {index < 2 && (
                                    <Text
                                        size={'small'}
                                        weight={'medium'}
                                        color={Colors.BASE_300}
                                        className={'mb-1 text-xs'}
                                    >
                                        {ratingScaleLabels[index]}
                                    </Text>
                                )}

                                <TextInput
                                    value={replaceDynamicTags({
                                        text:
                                            option.label[defaultLanguage] ||
                                            getLocalizedText(option.label) ||
                                            '',
                                        employee: previewEvaluee,
                                    })}
                                    size={'sm'}
                                    className={'text-xs'}
                                    bordered
                                    disabled
                                />

                                <div className={'mt-2'}>
                                    <OptionTranslationInput
                                        optionId={option.id}
                                        targetValue={option.label[targetLanguage] || ''}
                                        placeholder={t(
                                            'templateManagement.templateTranslation.enterTranslation',
                                            { language: languageDisplayName },
                                        )}
                                        onChange={(value: string) =>
                                            onOptionLabelChange(id ?? '', option.id, value)
                                        }
                                        className={'text-xs'}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {hasOptions && (
                <div>
                    <Text
                        weight={'medium'}
                        color={Colors.SIDEBAR_TEXT_STRONG}
                        className={'mb-3'}
                    >
                        {t('templateManagement.templateTranslation.optionsLabel')}
                    </Text>

                    <div className={'flex flex-col gap-3'}>
                        {options.map((option) => (
                            <div
                                className={'flex w-full'}
                                key={option.id}
                            >
                                <Separator
                                    orientation={'vertical'}
                                    thickness={'thin'}
                                    spacing={'none'}
                                    color={'base-300'}
                                    className={'mr-4'}
                                />

                                <div className={'flex w-full flex-col gap-1'}>
                                    <Text
                                        size={'small'}
                                        noWrap
                                        className={'text-xs'}
                                    >
                                        <span className={'mr-2'}>
                                            {t(
                                                'templateManagement.templateTranslation.optionLabel',
                                            )}
                                        </span>

                                        <QuestionTextRenderer
                                            text={replaceDynamicTags({
                                                text:
                                                    option.label[defaultLanguage] ||
                                                    getLocalizedText(option.label) ||
                                                    '',
                                                employee: previewEvaluee,
                                            })}
                                        />
                                    </Text>

                                    <div className={'w-full'}>
                                        <OptionTranslationInput
                                            optionId={option.id}
                                            targetValue={option.label[targetLanguage] || ''}
                                            placeholder={t(
                                                'templateManagement.templateTranslation.enterTranslation',
                                                { language: languageDisplayName },
                                            )}
                                            onChange={(value: string) =>
                                                onOptionLabelChange(id ?? '', option.id, value)
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};
